import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HeartIcon, MailIcon, PhoneIcon, UserIcon, MessageSquareIcon,
  EyeIcon, ShieldIcon, LockIcon, CheckIcon, UsersIcon, ActivityIcon,
} from '../assets/icons/Icons';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/contact.css';

const Contact = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState(''); // '', 'sending', 'sent', 'error'

  const updateField = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const response = await fetch('/api/contact/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (response.ok) {
        setStatus('sent');
        setForm({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <div className="contact-page">
        <div className="container">

          {/* Vision / Mission / Values — Bento Grid */}
          <div className="contact-header">
            <span className="section-tag">What we stand for</span>
            <h1>The heart behind the help.</h1>
            <p>Our foundation — the guiding principles behind every conversation, every care plan, and every step of your journey with us.</p>
          </div>

          <div className="vmv-bento">
            <div className="vmv-card vmv-vision">
              <div className="vmv-icon-wrap">
                <EyeIcon size={22} />
              </div>
              <span className="vmv-label">Vision</span>
              <h2>A companion for the road to recovery.</h2>
              <p>To be a trusted companion in your mental health journey, supporting meaningful and lasting recovery.</p>
            </div>

            <div className="vmv-card vmv-mission">
              <div className="vmv-icon-wrap">
                <HeartIcon size={22} />
              </div>
              <span className="vmv-label">Mission</span>
              <h2>Heard. Seen. Understood.</h2>
              <p>To provide evidence-based mental health care in a safe environment where every person feels heard, seen, understood, and supported.</p>
            </div>

            <div className="vmv-card vmv-values">
              <div className="vmv-icon-wrap">
                <ShieldIcon size={22} />
              </div>
              <span className="vmv-label">Values</span>
              <h2>What we live by, every day.</h2>
              <p>The principles that shape our practice.</p>
              <div className="vmv-values-grid">
                <div className="vmv-value-item">
                  <span className="vmv-value-bullet"><UsersIcon size={14} /></span>
                  <div>
                    <strong>Collaboration</strong>
                    <p>We believe the best outcomes are achieved through partnership. We work closely with individuals and their families to develop personalized treatment plans that reflect their unique needs, goals, and values.</p>
                  </div>
                </div>
                <div className="vmv-value-item">
                  <span className="vmv-value-bullet"><HeartIcon size={14} /></span>
                  <div>
                    <strong>Inclusivity</strong>
                    <p>We are committed to creating a welcoming and respectful environment where every individual is treated with empathy and dignity, regardless of their background, identity, or life experiences.</p>
                  </div>
                </div>
                <div className="vmv-value-item">
                  <span className="vmv-value-bullet"><LockIcon size={14} /></span>
                  <div>
                    <strong>Confidentiality &amp; Trust</strong>
                    <p>Trust is the foundation of effective care. We provide a safe and supportive space where individuals can seek help with confidence, knowing their privacy and well-being are respected.</p>
                  </div>
                </div>
                <div className="vmv-value-item">
                  <span className="vmv-value-bullet"><ActivityIcon size={14} /></span>
                  <div>
                    <strong>Innovation Meets Evidence</strong>
                    <p>Our approach combines the latest evidence-based psychiatric care with innovative therapeutic strategies, ensuring treatments are both scientifically grounded and tailored to individual needs.</p>
                  </div>
                </div>
                <div className="vmv-value-item">
                  <span className="vmv-value-bullet"><CheckIcon size={14} /></span>
                  <div>
                    <strong>Accessibility &amp; Continuity</strong>
                    <p>Mental health care is a journey, not a single appointment. We strive to provide accessible, consistent, and reliable support at every stage of recovery and well-being.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Header */}
          <div className="contact-header">
            <span className="section-tag">Get in touch</span>
            <h1>We'd love to hear from you</h1>
            <p>Have a question, need support, or want to schedule a visit? Reach out and we'll get back to you within 24 hours.</p>
          </div>

          <div className="contact-grid">

            {/* Contact Form */}
            <div className="contact-form-card">
              <h2>Send us a message</h2>

              {status === 'sent' ? (
                <div className="contact-success">
                  <div className="contact-success-icon">✓</div>
                  <h3>Message sent!</h3>
                  <p>Thank you for reaching out. We'll get back to you within 24 hours.</p>
                  <button
                    className="contact-submit"
                    onClick={() => setStatus('')}
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="contact-form-row">
                    <div className="form-group">
                      <label className="form-label">Your name</label>
                      <div className="form-input-wrap">
                        <input
                          className="form-input"
                          type="text"
                          placeholder="Full name"
                          value={form.name}
                          onChange={updateField('name')}
                          required
                        />
                        <span className="input-icon"><UserIcon size={16} /></span>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email address</label>
                      <div className="form-input-wrap">
                        <input
                          className="form-input"
                          type="email"
                          placeholder="you@example.com"
                          value={form.email}
                          onChange={updateField('email')}
                          required
                        />
                        <span className="input-icon"><MailIcon size={16} /></span>
                      </div>
                    </div>
                  </div>

                  <div className="contact-form-row">
                    <div className="form-group">
                      <label className="form-label">Phone (optional)</label>
                      <div className="form-input-wrap">
                        <input
                          className="form-input"
                          type="tel"
                          placeholder="+91 98765 43210"
                          value={form.phone}
                          onChange={updateField('phone')}
                        />
                        <span className="input-icon"><PhoneIcon size={16} color="currentColor" /></span>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Subject</label>
                      <div className="form-input-wrap">
                        <input
                          className="form-input no-icon"
                          type="text"
                          placeholder="What's this about?"
                          value={form.subject}
                          onChange={updateField('subject')}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Message</label>
                    <textarea
                      className="contact-textarea"
                      placeholder="Tell us how we can help..."
                      value={form.message}
                      onChange={updateField('message')}
                      required
                      rows={5}
                    />
                  </div>

                  {status === 'error' && (
                    <div className="contact-error">
                      Something went wrong. Please try again or email us directly.
                    </div>
                  )}

                  <button
                    type="submit"
                    className="contact-submit"
                    disabled={status === 'sending'}
                  >
                    {status === 'sending' ? 'Sending...' : 'Send message'}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info Sidebar */}
            <div className="contact-info-side">

              <div className="contact-info-card">
                <h3>Visit our clinic</h3>
                <p>Ishira, The Mind Clinic</p>
                <p>4th floor, Above YES Bank</p>
                <p>2nd Block, HRBR Layout</p>
                <p>Kalyan Nagar, Bengaluru 560043</p>
                <div className="contact-map-wrap">
                  <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4702.103706530919!2d77.6377175!3d13.0224553!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae17fc4dafc167%3A0x6191f9cef2cd6729!2sIshira%20The%20Mind%20Clinic!5e1!3m2!1sen!2sin!4v1779716218351!5m2!1sen!2sin"
                    width="100%"
                    height="120"
                    style={{ border: 0, borderRadius: '12px' }}
                    allowfullscreen=""
                    loading="lazy"
                    referrerpolicy="no-referrer-when-downgrade">
                    title="Ishira Clinic Location"
                  </iframe>
                </div>
              </div>

              <div className="contact-info-card">
                <h3>Contact details</h3>
                <div className="contact-detail-row">
                  <MailIcon size={16} />
                  <div>
                    <span className="contact-detail-label">Email</span>
                    <a href="mailto:care@ishiramind.com">care@ishiramind.com</a>
                  </div>
                </div>
                <div className="contact-detail-row">
                  <PhoneIcon size={16} color="currentColor" />
                  <div>
                    <span className="contact-detail-label">Phone</span>
                    <a href="tel:+919980810632">+91 99 8081 0632</a>
                  </div>
                </div>
                <div className="contact-detail-row">
                  <MessageSquareIcon size={16} />
                  <div>
                    <span className="contact-detail-label">WhatsApp</span>
                    <a href="https://wa.me/919980810632" target="_blank" rel="noopener noreferrer">+91 99 8081 0632</a>
                  </div>
                </div>
              </div>

              <div className="contact-info-card">
                <h3>Working hours</h3>
                <div className="hours-grid">
                  <span className="hours-day">Monday – Saturday</span>
                  <span className="hours-time">10:00 AM – 8:00 PM</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Contact;
